import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useMutation } from '@apollo/client';
import { Divider, Grid, Rating, Typography } from '@mui/material'

import { FAVOURITES_ICON_SOURCE } from '../../app/constants/constants';
import { UPDATE_RATING } from '../../app/graphql/mutations';
import { FavoritesRecipeSelector } from '../../app/homepage/homepage.slice';
import { useAppSelector } from '../../app/store/hooks';
import useLogRecipeEdit from '../../hooks/useLogRecipeEdit';
import { EventLogsEnum } from '../../utils/events';
import { FilledStarIconSmall, StarRatingMini, UnfilledStarIconSmall } from '../../utils/iconRefs/recipeRatingIcons';
import { resizeImageIfCloudinary } from '../../utils/images/cloudinaryUtils';
import Favorites from '../favourites/favouritesView';
import { getDateFormat } from '../recipePage/recipeUtils';
import styles from "./RatingsCard.module.css"

const RatingsCard = ({ sess }: { sess: any }) => {
    const history = useHistory();
    const userId = useAppSelector((state) => state.main.user?.id);
    const { logRecipeEdit } = useLogRecipeEdit();
    const [value, setValue] = useState<number>(0);
    const favoriteRecipeIds = useAppSelector(FavoritesRecipeSelector);

    const [updateRating] = useMutation(UPDATE_RATING);

    const handleOnRatingChange = (sessionId: string, newValue: number) => {
        setValue(newValue);
        logRecipeEdit(EventLogsEnum.APP_STAR_RATING, {
            sessionId,
            userId,
            recipeId: sess.recipeID,
            eventData: {
                rating: newValue,
            }
        });
        updateRating({
            variables: {
                sessionID: sessionId,
                rating: newValue,
            },
        });
    }

    const goToRecipeRatingPage = (sessionId: string) => {
        history.push(`/cooking-history/edit/${sessionId}`);
    }

    const redirectToRecipePage = (recipeId: string) => {
        history.push(`/recipe/${recipeId}`);

        logRecipeEdit(EventLogsEnum.APP_RECIPE_CARD_OPENED, {
            userId,
            recipeId,
        });
    }

    const handleShareMoreFeedback = () => {
        goToRecipeRatingPage(sess.sessionID);

        logRecipeEdit(EventLogsEnum.APP_SHARE_FEEDBACK_OPENED, {
            sessionId: sess.sessionID,
            recipe_id: sess.recipeID,
        });
    }

    const handleViewFeedback = () => {
        goToRecipeRatingPage(sess.sessionID);

        logRecipeEdit(EventLogsEnum.APP_VIEW_FEEDBACK_OPENED, {
            sessionId: sess.sessionID,
            recipe_id: sess.recipeID,
        });
    }

    const handleFavouritesClick = () => {
        const isUserFavorite = favoriteRecipeIds.includes(sess.recipeID);
        if (isUserFavorite) {
            logRecipeEdit(EventLogsEnum.APP_UN_FAVORITE_HISTORY_PAGE, {
                session_id: sess.sessionID,
                recipe_id: sess.recipeID,
            });
        }
        else {
            logRecipeEdit(EventLogsEnum.APP_ADD_FAVORITES_HISTORY_PAGE, {
                session_id: sess.sessionID,
                recipe_id: sess.recipeID,
            });
        }
    }

    const compressedImage = sess.sessionRecipe.Recipe_Variations[0] ? resizeImageIfCloudinary(sess.sessionRecipe.Recipe_Variations[0]?.image_url, 100, 100, -1, -1, true) : null;
    const formattedDate = getDateFormat(sess?.startTime);
    const defaultRating = (value === 0) && ((sess?.rating ?? 0) === 0) && (!sess.feedback || sess.feedback?.length === 0) && (!sess.userComments);

    const shareMore = ((value !== 0) || (sess.rating > 0)) && (!sess.feedback || sess.feedback?.length === 0) && (!sess.userComments) && (sess.feedback_images?.length === 0);

    return (
        <Grid className={styles.card}>
            <Grid className={styles.contentBox}>
                <Grid className={styles.content} onClick={() => redirectToRecipePage(sess.recipeID)}>
                    <Grid>
                        <img alt="img" className={styles.imgSize} src={compressedImage} />
                    </Grid>
                    <Grid className={styles.contentContainer}>
                        <Grid className={styles.contentText}>
                            <Typography variant="caption3Bold">
                                {sess.sessionRecipe.name}
                            </Typography>
                            <Typography className={styles.dateOfCooking} variant="ingredientText2paralell">
                                Cooked on {formattedDate}
                            </Typography>
                            {!!sess.sessionRecipe.recipe_market_price &&
                                <Typography className={styles.price} variant="ingredientText2paralell">
                                    Saved ₹{sess.sessionRecipe.recipe_market_price}
                                </Typography>
                            }
                        </Grid>
                        <Grid className={styles.favouriteIconWrapper}>
                            <Favorites handleCustomAction={handleFavouritesClick} recipeId={sess?.recipeID}
                                size={20} source={FAVOURITES_ICON_SOURCE.RATINGS_CARD} stroke="none" />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Divider />
            {defaultRating ?
                <Grid className={styles.rating}>
                    <Typography variant="ingredientText2paralell" >Rate your experience</Typography>
                    <Grid>
                        <Rating
                            className=""
                            emptyIcon={<UnfilledStarIconSmall />}
                            icon={<FilledStarIconSmall />}
                            name="customized-color"
                            onChange={(event, newValue) => handleOnRatingChange(sess.sessionID, newValue ?? 0)}
                            value={value}
                        />
                    </Grid>
                </Grid> :
                <Grid className={styles.rating}>
                    <Typography className={styles.subtitle} variant="ingredientText2paralell" >You have rated this <StarRatingMini /> {sess.rating > 0 ? sess.rating : value}</Typography>
                    {shareMore ?
                        <Typography
                            color="#3B1F8E"
                            onClick={handleShareMoreFeedback}
                            sx={{ textDecoration: 'underline' }}
                            variant="ingredientText2paralell" >
                            Share more feedback
                        </Typography> :
                        <Typography
                            color="#3B1F8E"
                            onClick={handleViewFeedback}
                            sx={{ textDecoration: 'underline' }}
                            variant="ingredientText2paralell" >
                            View feedback
                        </Typography>
                    }
                </Grid>
            }
        </Grid>
    )
}

export default RatingsCard